import React, { useState, useRef } from 'react';
import {
    Box,
    TextField,
    Typography,
    Button,
    Paper,
    IconButton,
    Chip,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Card,
    CardMedia,
    CardContent,
    CardActionArea
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Image as ImageIcon,
    Folder as FolderIcon
} from '@mui/icons-material';
import { useDataProvider } from 'react-admin';
import RichTextInput from './RichTextInput';

interface HotspotEditorProps {
    content: any;
    onChange: (content: any) => void;
}

interface Hotspot {
    id: string;
    x: number; // Percentage (0-100)
    y: number; // Percentage (0-100)
    radius: number; // Percentage
    label: string;
    feedback?: string;
}

const HotspotEditor: React.FC<HotspotEditorProps> = ({ content, onChange }) => {
    const dataProvider = useDataProvider();
    const imageRef = useRef<HTMLImageElement>(null);

    const instruction = content?.instruction || '';
    const learningContent = content?.learningContent || '';
    const imageUrl = content?.imageUrl || '';
    const mediaFileId = content?.mediaFileId || null;
    const hotspots: Hotspot[] = content?.hotspots || [];
    const [loadingImages, setLoadingImages] = useState(false);
    const [imageFiles, setImageFiles] = useState<any[]>([]);
    const [imageBlobUrl, setImageBlobUrl] = useState<string>('');
    const [imageDialogOpen, setImageDialogOpen] = useState(false);
    const [imagePreviews, setImagePreviews] = useState<{ [key: string]: string }>({});

    const isMediaLibrary = imageUrl && imageUrl.startsWith('/api/media/');

    // Load image from media library with authentication for preview
    React.useEffect(() => {
        if (isMediaLibrary && imageUrl) {
            const fetchImage = async () => {
                try {
                    const uuid = imageUrl.split('/').pop();
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/media/preview/${uuid}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });

                    if (response.ok) {
                        const blob = await response.blob();
                        const url = URL.createObjectURL(blob);
                        setImageBlobUrl(url);
                    } else {
                        console.error('Failed to load image:', response.status);
                    }
                } catch (error) {
                    console.error('Error loading image:', error);
                }
            };
            fetchImage();
        } else {
            setImageBlobUrl('');
        }

        // Cleanup blob URL on unmount or when imageUrl changes
        return () => {
            if (imageBlobUrl) {
                URL.revokeObjectURL(imageBlobUrl);
            }
        };
    }, [imageUrl, isMediaLibrary]);

    // Use blob URL if from media library, otherwise use direct URL
    const displayImageUrl = isMediaLibrary ? imageBlobUrl : imageUrl;

    const addHotspot = () => {
        const newHotspot: Hotspot = {
            id: Date.now().toString(),
            x: 50,
            y: 50,
            radius: 5,
            label: '',
            feedback: ''
        };
        onChange({ ...content, hotspots: [...hotspots, newHotspot] });
    };

    const updateHotspot = (hotspotId: string, field: string, value: any) => {
        const updatedHotspots = hotspots.map(hs =>
            hs.id === hotspotId ? { ...hs, [field]: value } : hs
        );
        onChange({ ...content, hotspots: updatedHotspots });
    };

    const deleteHotspot = (hotspotId: string) => {
        const updatedHotspots = hotspots.filter(hs => hs.id !== hotspotId);
        onChange({ ...content, hotspots: updatedHotspots });
    };

    const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!imageRef.current) return;

        const rect = imageRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        const newHotspot: Hotspot = {
            id: Date.now().toString(),
            x: Math.round(x * 10) / 10,
            y: Math.round(y * 10) / 10,
            radius: 5,
            label: `Hotspot ${hotspots.length + 1}`,
            feedback: ''
        };

        onChange({ ...content, hotspots: [...hotspots, newHotspot] });
    };

    const loadImageFiles = async () => {
        setLoadingImages(true);
        setImageDialogOpen(true);
        try {
            const response = await dataProvider.getList('media_files', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: 'createdAt', order: 'DESC' },
                filter: {}
            });

            const images = response.data.filter((file: any) => {
                const isImage = file.mimeType?.startsWith('image/');
                return isImage;
            });

            setImageFiles(images);

            // Load thumbnails for all images
            images.forEach((file: any) => {
                loadImageThumbnail(file);
            });
        } catch (err) {
            console.error('Error loading images:', err);
        } finally {
            setLoadingImages(false);
        }
    };

    const loadImageThumbnail = async (file: any) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/media/preview/${file.uuid}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                setImagePreviews(prev => ({ ...prev, [file.uuid]: url }));
            }
        } catch (error) {
            console.error('Error loading thumbnail:', error);
        }
    };

    const handleMediaFileSelect = (file: any) => {
        // Use UUID-based URL for media library files
        const url = `/api/media/${file.uuid}`;
        onChange({ ...content, mediaFileId: file.id, imageUrl: url });
        setImageDialogOpen(false);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Hotspot-Aufgabe (Image Map)</Typography>
            </Box>

            <Alert severity="info">
                Lernende klicken auf markierte Bereiche im Bild. Jeder Hotspot hat eine Beschreibung und Feedback.
            </Alert>

            {/* Learning content (optional) */}
            <RichTextInput
                label="Lerninhalt (optional)"
                value={learningContent}
                onChange={(val) => onChange({ ...content, learningContent: val })}
                helperText="Dieser formatierte Text wird im Player über dem Quiz angezeigt."
                placeholder="Optionaler Lernstoff, der über dem Quiz angezeigt wird..."
            />

            {/* Instruction */}
            <TextField
                label="Aufgabenstellung"
                fullWidth
                multiline
                rows={3}
                value={instruction}
                onChange={(e) => onChange({ ...content, instruction: e.target.value })}
                placeholder="Klicken Sie auf die markierten Bereiche im Bild..."
            />

            {/* Image Selection */}
            <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Bild auswählen</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        label="Bild-URL (direkt)"
                        fullWidth
                        value={imageUrl}
                        onChange={(e) => onChange({ ...content, imageUrl: e.target.value })}
                        placeholder="https://example.com/image.jpg"
                        helperText="Oder wählen Sie ein Bild aus der Mediathek"
                    />

                    <Button
                        variant="outlined"
                        startIcon={<ImageIcon />}
                        onClick={loadImageFiles}
                        disabled={loadingImages}
                    >
                        {loadingImages ? 'Lade Bilder...' : 'Aus Mediathek wählen'}
                    </Button>

                    {mediaFileId && (
                        <Alert severity="success">
                            Bild aus Mediathek ausgewählt
                        </Alert>
                    )}
                </Box>
            </Paper>

            {/* Image Preview with Hotspots */}
            {imageUrl && (
                <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Bild-Vorschau (Klicken Sie auf das Bild, um Hotspots hinzuzufügen)
                    </Typography>
                    {isMediaLibrary && !imageBlobUrl ? (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                Lade Bild...
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                position: 'relative',
                                width: '100%',
                                maxWidth: 800,
                                margin: '0 auto',
                                cursor: 'crosshair',
                                border: '2px dashed',
                                borderColor: 'primary.main'
                            }}
                            onClick={handleImageClick}
                        >
                            <img
                                ref={imageRef}
                                src={displayImageUrl}
                                alt="Hotspot Image"
                                style={{ width: '100%', display: 'block' }}
                                onError={() => alert('Bild konnte nicht geladen werden')}
                            />
                            {/* Render Hotspots */}
                            {hotspots.map((hs, index) => (
                                <Box
                                    key={hs.id}
                                    sx={{
                                        position: 'absolute',
                                        left: `${hs.x}%`,
                                        top: `${hs.y}%`,
                                        transform: 'translate(-50%, -50%)',
                                        width: `${hs.radius * 2}%`,
                                        height: `${hs.radius * 2}%`,
                                        borderRadius: '50%',
                                        border: '3px solid red',
                                        bgcolor: 'rgba(255, 0, 0, 0.2)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        pointerEvents: 'none'
                                    }}
                                >
                                    <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold' }}>
                                        {index + 1}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Paper>
            )}

            {/* Hotspots List */}
            <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1">Hotspots</Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={addHotspot}>
                        Manuell hinzufügen
                    </Button>
                </Box>

                {hotspots.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center">
                        Noch keine Hotspots definiert. Klicken Sie auf das Bild, um Hotspots hinzuzufügen.
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {hotspots.map((hs, index) => (
                            <Paper key={hs.id} sx={{ p: 2, bgcolor: 'background.default' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Chip label={`Hotspot ${index + 1}`} color="primary" size="small" />
                                        <TextField
                                            label="Bezeichnung"
                                            size="small"
                                            fullWidth
                                            required
                                            value={hs.label}
                                            onChange={(e) => updateHotspot(hs.id, 'label', e.target.value)}
                                            placeholder="z.B. Herz"
                                        />
                                        <IconButton
                                            onClick={() => deleteHotspot(hs.id)}
                                            color="error"
                                            size="small"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                                        <TextField
                                            label="X Position (%)"
                                            type="number"
                                            size="small"
                                            value={hs.x}
                                            onChange={(e) => updateHotspot(hs.id, 'x', parseFloat(e.target.value))}
                                            inputProps={{ min: 0, max: 100, step: 0.1 }}
                                        />
                                        <TextField
                                            label="Y Position (%)"
                                            type="number"
                                            size="small"
                                            value={hs.y}
                                            onChange={(e) => updateHotspot(hs.id, 'y', parseFloat(e.target.value))}
                                            inputProps={{ min: 0, max: 100, step: 0.1 }}
                                        />
                                        <TextField
                                            label="Radius (%)"
                                            type="number"
                                            size="small"
                                            value={hs.radius}
                                            onChange={(e) => updateHotspot(hs.id, 'radius', parseFloat(e.target.value))}
                                            inputProps={{ min: 1, max: 20, step: 0.5 }}
                                        />
                                    </Box>

                                    <TextField
                                        label="Feedback (wird beim Klick angezeigt)"
                                        size="small"
                                        fullWidth
                                        multiline
                                        rows={2}
                                        value={hs.feedback || ''}
                                        onChange={(e) => updateHotspot(hs.id, 'feedback', e.target.value)}
                                        placeholder="Erklärung zu diesem Bereich..."
                                    />
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                )}
            </Paper>

            {/* Summary */}
            {hotspots.length > 0 && imageUrl && (
                <Alert severity="success">
                    {hotspots.length} Hotspot(s) auf dem Bild definiert
                </Alert>
            )}

            {/* Image Selection Dialog */}
            <Dialog
                open={imageDialogOpen}
                onClose={() => setImageDialogOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ImageIcon />
                        <Typography variant="h6">Bild aus Mediathek wählen</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {loadingImages ? (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography>Lade Bilder...</Typography>
                        </Box>
                    ) : imageFiles.length === 0 ? (
                        <Alert severity="info">
                            Keine Bilder in der Mediathek gefunden.
                        </Alert>
                    ) : (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: 'repeat(2, 1fr)',
                                    md: 'repeat(3, 1fr)',
                                    lg: 'repeat(4, 1fr)'
                                },
                                gap: 2,
                                mt: 1
                            }}
                        >
                            {imageFiles.map(file => (
                                <Card key={file.id}>
                                    <CardActionArea onClick={() => handleMediaFileSelect(file)}>
                                        <CardMedia
                                            component="div"
                                            sx={{
                                                height: 200,
                                                bgcolor: 'grey.200',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            {imagePreviews[file.uuid] ? (
                                                <img
                                                    src={imagePreviews[file.uuid]}
                                                    alt={file.originalFilename}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                            ) : (
                                                <ImageIcon sx={{ fontSize: 60, color: 'grey.400' }} />
                                            )}
                                        </CardMedia>
                                        <CardContent>
                                            <Typography
                                                variant="body2"
                                                noWrap
                                                title={file.originalFilename}
                                            >
                                                {file.originalFilename}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {file.folder?.name || 'Hauptordner'}
                                            </Typography>
                                            <Box sx={{ mt: 0.5 }}>
                                                <Chip
                                                    label={new Date(file.createdAt).toLocaleDateString('de-DE')}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </Box>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setImageDialogOpen(false)}>
                        Abbrechen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default HotspotEditor;
